import { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { color, Icon, Input } from '@wf/components';
import { Grid } from '@wf/sw-ui-kit';

import Scrollable from '../../../../components/ListWithContentLayout/components/Scrollable';
import { ListItemState } from '../../../../components/ListWithContentLayout/types';
import { type IAttachedDocumentResponse } from '../../../License/types/response';
import ListWithContentLayout from '../../../../components/ListWithContentLayout';
import AttachedDocumentComparisonCard from './AttachedDocumentComparisonCard';
import { ArrayFields } from '../../../License/types/fieldEnums';
import Separator from '../../../../components/Separator';
import { Tab } from '../../../License/types/enums';
import ComparisonView from '../ComparisonView';

import useComparisonParams from '../../hooks/useComparisonParams';
import useListItems from '../../hooks/useListItems';

import { versionComparisonSelector } from '../../../../store/audit/selectors';
import attachedDocumentFields from '../../helpers/attachedDocumentFields';

import styles from './AttachedDocumentsComparison.module.scss';

function AttachedDocumentsComparison() {
  const { t } = useTranslation();
  const { lpcoResponse } = useSelector(versionComparisonSelector);
  const { leftEpochMillis, rightEpochMillis } = useComparisonParams();

  const [attachedDocuments, attachedDocumentStates] = useListItems(
    lpcoResponse.attachedDocuments ?? [],
    ArrayFields.attachedDocuments
  );

  const [selectedDocument, setSelectedDocument] = useState<{ values: IAttachedDocumentResponse; index: number } | null>(
    null
  );
  const [search, setSearch] = useState('');

  useEffect(() => {
    setSelectedDocument(null);
  }, [leftEpochMillis, rightEpochMillis]);

  const searchText = search.trim().toLowerCase();

  return (
    <ListWithContentLayout
      sidebar={
        <>
          <Grid gap={20} className="p-b-8">
            <span className="fs-20 fw-semibold typography">{t('All Documents')}</span>
            {attachedDocuments.length > 0 && (
              <Input
                leftIcon={<Icon name="ic_search" color={color('typography', 'light')} size={15} />}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('Search')}
              />
            )}
          </Grid>
          <Scrollable>
            <Grid gap={8}>
              {attachedDocuments.map((attachedDocument, index) => (
                <AttachedDocumentComparisonCard
                  onClick={() => setSelectedDocument({ values: attachedDocument, index })}
                  state={attachedDocumentStates[attachedDocument.id]}
                  isActive={selectedDocument?.index === index}
                  key={attachedDocument.id}
                  searchText={searchText}
                  index={index}
                  {...attachedDocument}
                />
              ))}
            </Grid>
          </Scrollable>
        </>
      }
    >
      <div className={styles.container} key={selectedDocument?.values.id}>
        {selectedDocument && (
          <>
            <div className="fs-20 fw-semibold  typography flex gap-8 items-center m-b-16">
              {t('Document Description')}
            </div>
            <Separator className={styles.separator} />
            <Scrollable>
              <ComparisonView
                showBothSides={
                  attachedDocumentStates[selectedDocument.values.id] !== ListItemState.Added &&
                  attachedDocumentStates[selectedDocument.values.id] !== ListItemState.Deleted
                }
                pathPrefix={`/${ArrayFields.attachedDocuments}/${selectedDocument.values.id}/`}
                values={selectedDocument.values}
                fields={attachedDocumentFields}
                tab={Tab.ATTACHED_DOCUMENT}
              />
            </Scrollable>
          </>
        )}
      </div>
    </ListWithContentLayout>
  );
}

export default AttachedDocumentsComparison;
